import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Checkbox,
  List,
  Modal,
  Spin,
  Tabs,
  Typography,
} from "antd";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  RegenerateUsernamesPreview,
  RegenerateUsernamesResult,
  SchoolEntity,
  SchoolEntityInput,
} from "../../../services/schools/school.types";
import {
  getApiErrorMessage,
  handleSubmitFormResponseManage,
  invalidateEntityList,
} from "../../../utils";
import "./schoolForm.scss";
import {
  createSchool,
  getCampuses,
  getSchools,
  regenerateStudentUsernames,
  updateSchool,
} from "../../../services/schools/school.service";
import { CampusRef } from "../../../services/schools/campus.types";
import { useSchools } from "../../../hooks/useSchools";
import { buildSchoolScalarPayload, initialValuesSchool } from "./utils";
import ActivityIndicator from "../../ActivityIndicator";
import { useNotification } from "../../../hooks/useNotification";
import { useGrades } from "../../../hooks/useGrades";
import { usePrograms } from "../../../hooks/usePrograms";
import { ProgramEntity } from "../../../services/programs/programs.types";
import {
  SchoolTab,
  SchoolStatusCard,
  KinderGraduationCard,
  GroupsTab,
  ClassesTab,
  UsersTab,
  CalendarTab,
  DosificationTab,
  ClassEntity,
} from "./components";
import TiendaTab from "./TiendaTab";
import AdoptionReportDrawer from "./AdoptionReport/AdoptionReportDrawer";
import CampusesTab from "./CampusesTab/CampusesTab";
import SectionGradeProgramGrid from "./SchoolsTab/SectionGradeProgramGrid";
import { getClassroomsBySchoolId } from "../../../services/classrooms/classrooms.service";
import {
  SelectionCell,
  CoordinatorInput,
} from "../../../services/classrooms/classrooms.types";

const { TabPane } = Tabs;

const REGENERATE_WARNING_ALERT_ID = "regenerate-usernames-warning";

// SCRUM-916: tabs que sólo existen en la escuela principal.
const CAMPUS_HIDDEN_TABS = ["calendar", "tienda", "dosification"];

const SchoolForm: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  // Route-provided id (true edit entry). Kept separate from the in-session
  // create id below so fetchInfo never re-keys off a school created mid-wizard.
  // Navigation state wins (the Schools list passes it); the /schools/:id URL
  // param is the fallback so a refresh or a shared link — where history state
  // may be absent — still opens a real edit session instead of creation mode.
  const paramSchoolId = Number(params.id);
  const routeSchoolId = ((location?.state as number) ??
    (Number.isFinite(paramSchoolId) ? paramSchoolId : undefined)) as number;
  const [createdSchoolId, setCreatedSchoolId] = useState<number | null>(null);
  // The id to use for everything downstream of creation (tab gating, child
  // props). Route id wins for true edit entry.
  const effectiveSchoolId = (routeSchoolId ?? createdSchoolId) as number;
  const { getSchoolById } = useSchools();
  const { loading: loadingGrades, grades } = useGrades();
  const { loading: loadingPrograms, programs } = usePrograms();
  const { notify } = useNotification();
  // antd v5's static notification does not render under React 19.
  const { notification } = App.useApp();

  // Estados para la escuela
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] =
    useState<SchoolEntityInput>(initialValuesSchool);
  // Grid selection (grade + program_id + groups per cell) — replaces the old
  // standalone "Gestión de Programas" multiselect. programSelected below is
  // now DERIVED from this state (distinct program_ids) so the existing
  // school POST/PATCH contract (SchoolEntityInput.programs) stays intact.
  const [generationSelections, setGenerationSelections] = useState<
    SelectionCell[]
  >([]);
  const [coordinator, setCoordinator] = useState<CoordinatorInput | undefined>(
    undefined
  );
  // Programs the school was already licensed for when loaded (edit mode).
  // The grid only EMITS new grade+program+groups selections — it never
  // receives existing programs as input — so previously-licensed programs
  // must be tracked separately and unioned with the live grid selections.
  const [existingProgramIds, setExistingProgramIds] = useState<number[]>([]);
  // Licensing = distinct program_ids present in the grid, unioned with
  // programs already licensed before this edit session (design ADR-7,
  // additive-only licensing).
  const programSelected = useMemo(() => {
    const distinctProgramIds = Array.from(
      new Set([
        ...existingProgramIds,
        ...generationSelections.map((cell) => cell.program_id),
      ])
    );
    return distinctProgramIds.map((id) => {
      const program = programs.find((p) => p.id === id);
      return { id, title: program?.title || "" };
    });
  }, [generationSelections, existingProgramIds, programs]);
  // Programs the school is ACTUALLY LICENSED for (persisted SchoolToProgram)
  // — existingProgramIds only, excluding transient grid picks from
  // generationSelections that haven't been generated/persisted yet. Gates
  // the manual add/edit class program selector (single-create endpoint has
  // no license validation, so this is the reachable frontend guard).
  const licensedPrograms = useMemo(() => {
    return existingProgramIds.map((id) => {
      const program = programs.find((p) => p.id === id);
      return { id, title: program?.title || "" };
    });
  }, [existingProgramIds, programs]);
  // SCRUM-804: los mismos programas licenciados, pero completos — la
  // dosificación necesita grado y asignatura, que licensedPrograms descarta.
  const licensedProgramEntities = useMemo(
    () =>
      existingProgramIds
        .map((id) => programs.find((program) => program.id === id))
        .filter((program): program is ProgramEntity => Boolean(program)),
    [existingProgramIds, programs]
  );
  // SCRUM-922: los niveles que la escuela tiene contratados salen de los
  // GRADOS de sus programas licenciados, que es lo que de verdad determina qué
  // se le enseña. Deliberadamente NO usa mostrar_preescolar/mostrar_primaria:
  // esas dos son de la tienda y están en true en casi todas las escuelas, así
  // que no recortarían nada. Sin programas licenciados (alta de escuela, o la
  // lista todavía cargando) se muestran los dos niveles: esconder los cuatro
  // interruptores sería peor que mostrarlos de más.
  const contractedLevels = useMemo(() => {
    const gradeCodes = licensedProgramEntities.map(
      (program) => program.grade?.code ?? ""
    );
    if (gradeCodes.length === 0) return { kinder: true, primaria: true };
    return {
      kinder: gradeCodes.some((code) => code.startsWith("K")),
      primaria: gradeCodes.some((code) => !code.startsWith("K")),
    };
  }, [licensedProgramEntities]);
  const [groups, setGroups] = useState<string[]>([]);
  const [originalGroups, setOriginalGroups] = useState<string[]>([]);
  const [originalAbbreviation, setOriginalAbbreviation] = useState("");
  // SCRUM-1034: todas las escuelas vivas (principales y planteles) para que el
  // campo Abreviatura avise en el momento si ya está en uso. Si la carga
  // falla, la lista queda vacía y la validación la hace sólo el backend, que
  // responde 409 con el mismo aviso.
  const [existingSchools, setExistingSchools] = useState<SchoolEntity[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const schools = await getSchools();
        if (!cancelled) setExistingSchools(schools ?? []);
      } catch {
        // El backend sigue rechazando el duplicado; acá no hay nada que avisar.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const otherSchools = useMemo(
    () => existingSchools.filter((school) => school.id !== effectiveSchoolId),
    [existingSchools, effectiveSchoolId]
  );
  const [pendingSchoolInput, setPendingSchoolInput] =
    useState<SchoolEntityInput | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [preview, setPreview] = useState<RegenerateUsernamesPreview | null>(
    null
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [pendingUpdateResponse, setPendingUpdateResponse] =
    useState<SchoolEntity | null>(null);
  const [result, setResult] = useState<RegenerateUsernamesResult | null>(
    null
  );
  const [resultError, setResultError] = useState<string | null>(null);
  const previewRequestIdRef = useRef(0);
  // Access switch (SCRUM-748). Kept OUT of initialValues on purpose: it is not
  // part of the school form payload, so saving the form can never flip access.
  // Defaults to active so a backend that predates the flag never renders the
  // school as "Inactiva".
  const [isActive, setIsActive] = useState(true);
  // Section flags (SCRUM-842): CalendarTab shows one calendar tab per section
  // the school has. Also outside initialValues — read-only here.
  const [schoolSections, setSchoolSections] = useState<{
    mostrar_preescolar?: boolean;
    mostrar_primaria?: boolean;
  }>({});
  // SCRUM-916: mismo patrón read-only que schoolSections — se carga en
  // loadSchool y NUNCA entra a initialValues/schoolInput, porque un PATCH que
  // lo llevara pisaría la jerarquía en cada guardado.
  const [parentId, setParentId] = useState<number | null>(null);
  const [parentName, setParentName] = useState<string | null>(null);
  const isCampus = parentId !== null;
  // SCRUM-916: árbol de planteles de la raíz. Vacío en una escuela sin hijas
  // (147 de 148) y en modo plantel — así Clases y Usuarios no muestran NADA
  // nuevo cuando no hay planteles que distinguir.
  const [campuses, setCampuses] = useState<CampusRef[]>([]);

  // Estados para las clases
  const [classes, setClasses] = useState<ClassEntity[]>([]);
  // Reconstructs the grid's selection on edit re-entry: grade_id -> distinct,
  // non-deleted program_ids already materialized as classrooms. When the
  // school has no classes yet but the wizard carries an upfront selection
  // (made in the creation grid before saving), fall back to that selection —
  // the ClassesTab grid instance mounts fresh after create and would
  // otherwise seed blank while generationSelections still held the picks.
  const initialGridSelection = useMemo(() => {
    const byGrade: Record<number, number[]> = {};
    // SCRUM-1039: a ClassroomToProgram row can be stale — editing a program's
    // grade after it was licensed leaves the classroom's grade_id out of sync
    // with the program's TRUE grade. One such row used to hard-block class
    // generation for the WHOLE school, so both branches funnel through here.
    // An empty `programs` means the catalogue has not loaded yet, not that no
    // program is valid — validating then would wipe a correct grid to empty.
    const canValidate = programs.length > 0;
    const isConsistentPair = (gradeId: number, programId: number) => {
      if (!canValidate) return true;
      const program = programs.find((p) => p.id === programId);
      return (
        program !== undefined &&
        (program.grade?.id ?? program.grade_id) === gradeId
      );
    };
    const addPair = (gradeId: number, programId: number) => {
      if (!isConsistentPair(gradeId, programId)) {
        console.warn(
          `SchoolForm: par grado/programa inconsistente descartado (grade_id=${gradeId}, program_id=${programId})`
        );
        return;
      }
      const existing = byGrade[gradeId] ?? [];
      if (!existing.includes(programId)) {
        byGrade[gradeId] = [...existing, programId];
      }
    };
    classes.forEach((classEntity) => {
      if (classEntity.deleted_at) return;
      addPair(classEntity.grade_id, classEntity.program_id);
    });
    if (Object.keys(byGrade).length === 0) {
      generationSelections.forEach((cell) => {
        addPair(cell.grade_id, cell.program_id);
      });
    }
    return byGrade;
  }, [classes, generationSelections, programs]);
  // Per-grade GROUPS for that same rehydration path. Groups are per grade
  // (SCRUM-673) and live in the grid's own state, so a remount loses them —
  // and initialGridSelection carries program_ids only. generationSelections
  // is the one thing that survives the remount holding SelectionCell.groups,
  // so without this every grade silently reset to a single default "A",
  // discarding names the operator had typed (e.g. PLATA / BRONCE).
  const initialGridGroups = useMemo(() => {
    const byGrade: Record<number, string[]> = {};
    generationSelections.forEach((cell) => {
      const existing = byGrade[cell.grade_id] ?? [];
      cell.groups.forEach((group) => {
        const alreadyThere = existing.some(
          (name) => name.trim().toLowerCase() === group.trim().toLowerCase()
        );
        if (!alreadyThere) existing.push(group);
      });
      byGrade[cell.grade_id] = existing;
    });
    return byGrade;
  }, [generationSelections]);
  // Creation-flow handoff: raised after creating a school with an upfront
  // grid selection so ClassesTab auto-opens the generation confirmation.
  const [autoOpenGenerate, setAutoOpenGenerate] = useState(false);
  const submitInFlightRef = useRef(false);
  const regenerateInFlightRef = useRef(false);
  const [editingClassId, setEditingClassId] = useState<number | null>(null);
  const [editingClass, setEditingClass] = useState<ClassEntity | null>(null);

  // Estado para las tabs
  const [activeTab, setActiveTab] = useState("schools");
  // SCRUM-1557: drawer del reporte de adopción de esta escuela.
  const [adoptionReportOpen, setAdoptionReportOpen] = useState(false);

  // Función para manejar el cambio de tab que solo permite cambiar si hay schoolId
  const handleTabChange = (key: string) => {
    if (!effectiveSchoolId && key !== "schools") {
      // Si no hay schoolId, no permitir cambiar a otras pestañas
      return;
    }
    // SCRUM-916: calendario, tienda y dosificación son de la escuela
    // principal. Las tabs ya no se renderizan en un plantel; esto corta
    // además un deep-link viejo que traiga la key en la mano.
    if (isCampus && CAMPUS_HIDDEN_TABS.includes(key)) {
      return;
    }

    setActiveTab(key);
  };

  // Loads the PERSISTED state of a school (initialValues, existingProgramIds,
  // groups, classes) from the backend. Used both by the route-id fetch effect
  // and by the wizard flow (after create and after generate) so downstream
  // consumers of existingProgramIds/programSelected (e.g. ClassesTab) reflect
  // what the backend actually holds, not just in-memory grid selections.
  // Intentionally does NOT touch generationSelections/coordinator — those are
  // wizard-only inputs the "Generar clases" modal still needs.
  /**
   * SCRUM-916: `GET /classrooms/school/:id` responde SÓLO las clases de esa
   * fila School. Con planteles, la tab Clases de la raíz tiene que ver el
   * árbol entero (si no, el selector y la columna Plantel no filtran nada y
   * "Mover a plantel" deja la clase invisible). Sin planteles es una sola
   * llamada, idéntica a la de siempre.
   */
  const fetchClassesOfTree = async (
    schoolId: number,
    tree: CampusRef[]
  ): Promise<ClassEntity[]> => {
    const schoolIds = tree.length > 1 ? tree.map((campus) => campus.id) : [schoolId];
    const responses = await Promise.all(
      schoolIds.map((id) => getClassroomsBySchoolId(id))
    );
    return responses.flat().map((classroom) => ({
      id: classroom.id,
      grade_id: classroom.grade_id,
      program_id: classroom.program_id,
      class_id: classroom.custom_id || "", // Mapear custom_id a class_id
      class_code: classroom.custom_code,
      custom_code: classroom.custom_code,
      custom_id: classroom.custom_id,
      school_id: classroom.school_id,
    }));
  };

  const loadSchool = async (id: number) => {
    const response = await getSchoolById(id);
    let loadedCampuses: CampusRef[] = [];
    if (response) {
      setInitialValues({
        cct: response.cct,
        city: response.city,
        demo: response.demo,
        // May be undefined while the backend predates SCRUM-611; left as-is
        // so edit PATCHes omit the key (undefined is dropped from JSON) and
        // never fabricate a value the backend didn't return.
        diary_type: response.diary_type,
        // SCRUM-907: same rule as diary_type — a backend that predates the
        // column returns undefined, and the PATCH omits the key instead of
        // inventing a false.
        primaria_diary_enabled: response.primaria_diary_enabled,
        // SCRUM-795 / SCRUM-922: igual que diary_type, se dejan tal como
        // vinieron. Un backend anterior al ticket devuelve undefined y el PATCH
        // omite la clave en vez de inventar un false.
        kinder_math_without_manipulatives:
          response.kinder_math_without_manipulatives,
        kinder_literacy_without_manipulatives:
          response.kinder_literacy_without_manipulatives,
        primaria_math_without_manipulatives:
          response.primaria_math_without_manipulatives,
        primaria_literacy_without_manipulatives:
          response.primaria_literacy_without_manipulatives,
        name: response.name,
        abbreviation: response.abbreviation || "",
        programs: response.programs || [],
        groups: response.groups || [],
      });
      setOriginalAbbreviation(response.abbreviation || "");
    }

    if (response) {
      setIsActive(response.is_active !== false);
      setSchoolSections({
        mostrar_preescolar: response.mostrar_preescolar,
        mostrar_primaria: response.mostrar_primaria,
      });
      const loadedParentId = response.parent_id ?? null;
      setParentId(loadedParentId);
      setParentName(null);
      // SCRUM-916: el árbol sólo se resuelve desde la raíz. En un plantel no
      // hay selector ni columna: la vista ya es la de un único plantel.
      if (loadedParentId === null) {
        try {
          loadedCampuses = (await getCampuses(id)) ?? [];
        } catch (campusError) {
          console.error("Error loading campuses:", campusError);
        }
        setCampuses(loadedCampuses);
      } else {
        setCampuses([]);
      }
      if (loadedParentId !== null) {
        // Venir de la tab Tienda de una raíz a un plantel dejaría una activeKey
        // sin TabPane: antd renderiza el panel vacío en vez de avisar.
        setActiveTab((current) =>
          CAMPUS_HIDDEN_TABS.includes(current) ? "schools" : current
        );
        // GET /schools/:id trae parent_id pero no el NOMBRE de la principal, y
        // el listado de planteles resuelve el árbol desde cualquier id — una
        // sola llamada da la raíz. Si falla, el banner se muestra igual sin el
        // nombre: la jerarquía no puede depender de un fetch decorativo.
        try {
          const campuses = await getCampuses(loadedParentId);
          setParentName(
            campuses.find((campus) => campus.is_main)?.name ?? null
          );
        } catch (campusError) {
          console.error("Error loading parent school name:", campusError);
        }
      }
    }

    if (response?.programs) {
      setExistingProgramIds(response.programs.map((program) => program.id));
    }

    if (response?.groups) {
      setGroups(response.groups);
      setOriginalGroups([...response.groups]);
    }

    // Cargar las clases existentes para esta escuela
    try {
      setClasses(await fetchClassesOfTree(id, loadedCampuses));
    } catch (classroomError: any) {
      console.error("Error loading classrooms:", classroomError);
      notify(
        "warning",
        "Advertencia",
        getApiErrorMessage(
          classroomError,
          "No se pudieron cargar las clases de la escuela. Los datos de clases y usuarios podrían estar incompletos."
        )
      );
    }
  };

  const fetchInfo = async () => {
    setLoading(true);
    try {
      if (routeSchoolId) {
        await loadSchool(routeSchoolId);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      notify(
        "error",
        "Error",
        getApiErrorMessage(
          error,
          "No se pudo cargar la información. Reintentá o volvé al listado."
        )
      );
    }
  };

  const onFinish = async () => {
    // Synchronous re-entrancy guard: `loading` lags a render behind, so a
    // fast double click on Guardar would fire two createSchool calls.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      await submitSchool();
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const submitSchool = async () => {
    // Guardar/Actualizar Escuela only touches school SCALAR data now —
    // programs and groups both moved to Clases/Programas. schoolInput below
    // intentionally OMITS programs/groups (not empty arrays — actually
    // absent keys) so the backend's update() guard (`if (programs
    // !== undefined)` / `if (groups !== undefined)`) skips its diff entirely.
    // CRITICAL: sending these as empty/stale arrays previously let a
    // premature "Actualizar Escuela" (e.g. right after dismissing
    // GenerarClasesModal mid-generation, before loadSchool() refreshed
    // existingProgramIds/groups) cascade soft-delete the just-generated
    // classrooms and/or groups.
    // SCRUM-750: there is deliberately NO licensedPrograms validation here.
    // Guarding a field this payload does not carry only ever produced false
    // negatives — an unresolvable program title locked the operator out of
    // every scalar edit, the Escuela Demo toggle included.
    // Every school-level scalar comes from buildSchoolScalarPayload, shared
    // with onCommitPrograms so a new flag can never reach only one of the two
    // PATCHes. undefined values (old backend, edit mode) are dropped from the
    // JSON payload, so the backend keeps/derives its own defaults.
    const schoolInput: SchoolEntityInput = buildSchoolScalarPayload(
      initialValues,
      isCampus
    );

    if (!effectiveSchoolId) {
      // Creating a school: create the shell WITHOUT navigating away, then move
      // the wizard into edit context for the new id and jump to Clases, which
      // is now the single home for the generation grid + "Generar clases".
      // generationSelections/coordinator are intentionally left untouched.
      try {
        setLoading(true);
        // Groups picked upfront in the creation grid: the fresh school has
        // none persisted, so the post-create reload below would wipe them —
        // snapshot and restore. originalGroups stays [] (the persisted
        // truth), which correctly keeps every group editable/unlocked and
        // lets the generation modal persist them all as new.
        const wizardGroups = groups;
        // POST /schools requires `programs` (@IsArray, not optional) — an
        // explicit empty array is the creation semantic (license later via
        // generation). The omit-programs guard protects UPDATE diffs only.
        const response = await createSchool({ ...schoolInput, programs: [] });
        if (response?.id) {
          dispatch({ type: "school/addSchool", payload: response });
          notify("success", "Éxito", "Escuela creada correctamente.");
          setCreatedSchoolId(response.id);
          // Reload the persisted school (existingProgramIds/groups/classes) so
          // downstream tabs reflect what was actually saved. Does NOT touch
          // generationSelections/coordinator — the "Generar clases" modal
          // still needs them.
          await loadSchool(response.id);
          if (wizardGroups.length > 0) {
            setGroups(wizardGroups);
          }
          if (generationSelections.length > 0) {
            setAutoOpenGenerate(true);
          }
          // Reflect the created school in the address bar WITHOUT a router
          // navigation: navigate() to /schools/:id would remount this form
          // and drop the wizard handoff (selections/groups/auto-open). A
          // plain history replace keeps the component alive; `usr` is where
          // React Router v6 stores location.state, so refreshing this URL
          // restores a true edit session via both the state and the :id
          // param fallback above.
          window.history.replaceState(
            { ...window.history.state, usr: response.id },
            "",
            `/schools/${response.id}`
          );
          setActiveTab("classes");
        } else {
          notify("error", "Error", "Ocurrió un error al crear la escuela.");
        }
      } catch (error) {
        console.error(error);
        // The backend now answers with actionable messages (duplicate CCT →
        // 409, missing field → 400 with the field list) — show them instead
        // of a blind generic.
        notify(
          "error",
          "Error",
          getApiErrorMessage(error, "Ocurrió un error al crear la escuela.")
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (routeSchoolId) {
      const abbreviationChanged =
        initialValues.abbreviation.trim() !== originalAbbreviation.trim();

      if (abbreviationChanged) {
        setPendingSchoolInput(schoolInput);
        setAckChecked(false);
        setPreview(null);
        setConfirmOpen(true);
        fetchRegenerateUsernamesPreview();
        return;
      }

      // True edit entry: keep the shared navigate-away behavior.
      try {
        setLoading(true);
        await handleSubmitFormResponseManage(
          schoolInput,
          createSchool,
          updateSchool,
          navigate,
          dispatch,
          "school",
          "addSchool",
          "updateSchool",
          routeSchoolId
        );
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // In-session re-save: effectiveSchoolId came from createdSchoolId (no
    // route id), meaning the school was already created earlier in this
    // wizard session. Re-saving must UPDATE that same school in place — NOT
    // re-enter the create branch, which would either hit School.cct's unique
    // constraint (same CCT) or silently create a second, orphaned school
    // (changed CCT) while the coordinator/classrooms already generated stay
    // attached to the first id. Stays on the current tab; no navigation.
    try {
      setLoading(true);
      const response = await updateSchool(createdSchoolId as number, schoolInput);
      if (response) {
        notify("success", "Éxito", "Escuela actualizada correctamente.");
        await loadSchool(createdSchoolId as number);
      } else {
        notify("error", "Error", "Ocurrió un error al actualizar la escuela.");
      }
    } catch (error) {
      console.error(error);
      notify(
        "error",
        "Error",
        getApiErrorMessage(error, "Ocurrió un error al actualizar la escuela.")
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchRegenerateUsernamesPreview = async () => {
    if (!routeSchoolId) return;
    const requestId = ++previewRequestIdRef.current;
    setPreviewLoading(true);
    try {
      const response = await regenerateStudentUsernames(
        routeSchoolId,
        true,
        initialValues.abbreviation
      );
      if (requestId !== previewRequestIdRef.current) return;
      if (response?.dryRun) {
        setPreview(response);
      }
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) return;
      console.error("Error fetching regenerate-usernames preview:", error);
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const handleCancelConfirm = () => {
    previewRequestIdRef.current += 1;
    setConfirmOpen(false);
    setPendingSchoolInput(null);
    setAckChecked(false);
    setPreview(null);
  };

  const finishSuccessfulSave = (response: SchoolEntity) => {
    dispatch({
      type: "school/updateSchool",
      payload: { id: routeSchoolId, data: response },
    });
    invalidateEntityList(dispatch, "school");
    notification.success({
      message: "Éxito",
      description: "Actualización exitosa.",
      placement: "top",
    });
    navigate(-1);
  };

  const handleConfirmRegenerate = async () => {
    if (!routeSchoolId || !pendingSchoolInput) return;
    if (regenerateInFlightRef.current) return;
    regenerateInFlightRef.current = true;
    setRegenerating(true);
    dispatch({ type: "school/setLoading", payload: true });
    try {
      let updateResponse: SchoolEntity | null = null;
      try {
        updateResponse = await updateSchool(routeSchoolId, pendingSchoolInput);
      } catch (error) {
        notify(
          "error",
          "Error",
          getApiErrorMessage(error, "Ocurrió un error al actualizar.")
        );
        setConfirmOpen(false);
        return;
      }

      if (!updateResponse) {
        notify("error", "Error", "Ocurrió un error al actualizar.");
        setConfirmOpen(false);
        return;
      }

      if (!ackChecked) {
        setConfirmOpen(false);
        finishSuccessfulSave(updateResponse);
        return;
      }

      setPendingUpdateResponse(updateResponse);
      setConfirmOpen(false);
      try {
        const regenerateResponse = await regenerateStudentUsernames(
          routeSchoolId
        );
        setResultError(null);
        setResult(
          regenerateResponse && regenerateResponse.dryRun === false
            ? regenerateResponse
            : null
        );
      } catch (error) {
        setResult(null);
        setResultError(
          getApiErrorMessage(
            error,
            "Ocurrió un error al actualizar los usuarios."
          )
        );
      }
    } finally {
      setRegenerating(false);
      dispatch({ type: "school/setLoading", payload: false });
      regenerateInFlightRef.current = false;
    }
  };

  const handleRetryRegenerate = async () => {
    if (!routeSchoolId) return;
    if (regenerateInFlightRef.current) return;
    regenerateInFlightRef.current = true;
    setRegenerating(true);
    dispatch({ type: "school/setLoading", payload: true });
    try {
      const regenerateResponse = await regenerateStudentUsernames(
        routeSchoolId
      );
      setResultError(null);
      setResult(
        regenerateResponse && regenerateResponse.dryRun === false
          ? regenerateResponse
          : null
      );
    } catch (error) {
      setResult(null);
      setResultError(
        getApiErrorMessage(
          error,
          "Ocurrió un error al actualizar los usuarios."
        )
      );
    } finally {
      setRegenerating(false);
      dispatch({ type: "school/setLoading", payload: false });
      regenerateInFlightRef.current = false;
    }
  };

  const handleDismissResult = () => {
    const updateResponse = pendingUpdateResponse;
    const hadError = resultError !== null;
    const totalNonThrowingFailure =
      !hadError &&
      result !== null &&
      result.total > 0 &&
      result.updated === 0 &&
      result.failed.length === result.total;
    setResult(null);
    setResultError(null);
    setPendingUpdateResponse(null);
    setPendingSchoolInput(null);
    setAckChecked(false);
    setPreview(null);
    if (hadError) {
      notify(
        "error",
        "Error",
        "No se pudieron actualizar los usuarios. La abreviatura sí se guardó."
      );
      return;
    }
    if (totalNonThrowingFailure) {
      notify(
        "error",
        "Error",
        "No se pudo actualizar ningún usuario. La abreviatura sí se guardó."
      );
      return;
    }
    if (updateResponse) {
      finishSuccessfulSave(updateResponse);
    }
  };

  useEffect(() => {
    if (!confirmOpen) return;
    const applyAriaDescribedBy = () => {
      const dialog = document.querySelector<HTMLElement>(
        '.regenerate-usernames-confirm[role="dialog"]'
      );
      dialog?.setAttribute("aria-describedby", REGENERATE_WARNING_ALERT_ID);
    };
    applyAriaDescribedBy();
    const timer = setTimeout(applyAriaDescribedBy, 0);
    return () => clearTimeout(timer);
  }, [confirmOpen]);

  // Keyed on the ROUTE id only — an in-session create must NOT re-trigger
  // this effect, which would refetch and clobber the in-memory wizard state
  // (groups/classes) held for the newly-created school.
  useEffect(() => {
    // Insurance for the in-session created school: should its id ever
    // materialize as a route id (e.g. a router state sync after the URL
    // replace), everything is already loaded — refetching would wipe the
    // restored wizard groups and race the generation handoff.
    if (routeSchoolId && routeSchoolId === createdSchoolId) return;
    // React Router does NOT remount this component when only the target id
    // changes (back/forward between two /schools/:id entries), so wizard
    // inputs — all per-school — must be reset by hand or they leak into the
    // next school (e.g. generating School B's classes from School A's picks).
    setGenerationSelections([]);
    setCoordinator(undefined);
    setEditingClassId(null);
    setEditingClass(null);
    setAutoOpenGenerate(false);
    setCreatedSchoolId(null);
    // Sin esto, ir de un plantel a una raíz por el botón "Ir a la escuela
    // principal" deja el banner y el modo plantel del anterior: React Router
    // no remonta este componente cuando sólo cambia el id.
    setParentId(null);
    setParentName(null);
    fetchInfo();
    // createdSchoolId intentionally omitted: its change must never re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSchoolId]);

  // Direct program management (Programas multiselect on the Escuela tab,
  // edit mode only): add/remove without going through the generation
  // wizard. There is no dedicated program add/remove endpoint —
  // updateSchool's diff against the persisted SchoolToProgram set
  // (present-before-not-in-payload => removed + cascade; new => added) is the
  // only way to mutate it, so this builds the full SchoolEntityInput with the
  // caller-supplied program id set and reuses that diff. Reloads afterward so
  // existingProgramIds/classes reflect the backend's cascade (e.g. removed
  // classrooms). groups is intentionally OMITTED — this control manages
  // programs only and must never diff/cascade groups (same class of bug as
  // onFinish).
  const onCommitPrograms = async (newProgramIds: number[]) => {
    if (!effectiveSchoolId) return;

    const payload: SchoolEntityInput = {
      // Same scalars as onFinish, from the same builder: this PATCH resends
      // the whole school, so anything it left out would be switched off just
      // by touching Programas.
      ...buildSchoolScalarPayload(initialValues, isCampus),
      programs: newProgramIds.map((id) => {
        const program = programs.find((p) => p.id === id);
        return { id, title: program?.title || "" };
      }),
    };

    try {
      const response = await updateSchool(effectiveSchoolId, payload);
      if (response) {
        await loadSchool(effectiveSchoolId);
        notify("success", "Éxito", "Programas actualizados correctamente.");
      } else {
        notify(
          "error",
          "Error",
          "Ocurrió un error al actualizar los programas."
        );
      }
    } catch (error) {
      console.error("Error updating school programs:", error);
      notify(
        "error",
        "Error",
        getApiErrorMessage(
          error,
          "Ocurrió un error al actualizar los programas."
        )
      );
    }
  };

  // Función para refrescar las clases
  const refreshClasses = async () => {
    if (!effectiveSchoolId) return;
    try {
      setClasses(await fetchClassesOfTree(effectiveSchoolId, campuses));
    } catch (error) {
      console.error("Error refreshing classes:", error);
    }
  };

  const isLoading = loading || loadingGrades || loadingPrograms;

  return (
    <div className="school-form-container">
      {isLoading ? (
        <ActivityIndicator />
      ) : (
        <>
          <div className="school-form-title">
            <Typography.Title level={3}>
              {effectiveSchoolId
                ? initialValues.name || "Editar Escuela"
                : "Crear Escuela"}
            </Typography.Title>
            {/* SCRUM-1557: el reporte es de la escuela principal con todos
                sus planteles, igual que Calendario, Tienda y Dosificación. */}
            {effectiveSchoolId && !isCampus && (
              <Button onClick={() => setAdoptionReportOpen(true)}>
                Reporte de adopción
              </Button>
            )}
          </div>
          {/* Montado sólo abierto: sus consultas no corren con la ficha. */}
          {effectiveSchoolId && !isCampus && adoptionReportOpen && (
            <AdoptionReportDrawer
              open={adoptionReportOpen}
              onClose={() => setAdoptionReportOpen(false)}
              schoolId={effectiveSchoolId}
              schoolName={initialValues.name}
              showKinder={schoolSections.mostrar_preescolar ?? true}
              showPrimaria={schoolSections.mostrar_primaria ?? true}
            />
          )}
          {isCampus && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                parentName
                  ? `Plantel de ${parentName}`
                  : "Plantel de otra escuela"
              }
              description="La configuración (calendario, tienda, dosificación, ajustes y programas) se administra desde la escuela principal."
              action={
                <Button
                  type="link"
                  onClick={() =>
                    // `state` es el contrato de nav-state del que depende
                    // routeSchoolId: sin él la principal abriría en modo alta.
                    navigate(`/schools/${parentId}`, { state: parentId })
                  }
                >
                  Ir a la escuela principal
                </Button>
              }
            />
          )}
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="school-tabs"
            size="large"
          >
            <TabPane tab="Escuela" key="schools">
              <SchoolTab
                initialValues={initialValues}
                setInitialValues={setInitialValues}
                coordinator={coordinator}
                setCoordinator={setCoordinator}
                onFinish={onFinish}
                loading={loading}
                isUpdate={Boolean(effectiveSchoolId)}
                licensedProgramIds={existingProgramIds}
                classes={classes}
                onCommitPrograms={onCommitPrograms}
                hasKinder={contractedLevels.kinder}
                hasPrimaria={contractedLevels.primaria}
                isCampus={isCampus}
                otherSchools={otherSchools}
                originalAbbreviation={originalAbbreviation}
              >
                {/* Creation only: the class generator is available upfront so a
                    school can be created together with its classes, groups and
                    program licensing in one flow. Once the school exists, the
                    grid lives exclusively in the Clases tab. */}
                {!effectiveSchoolId && (
                  <div
                    className="creation-generation-section"
                    style={{ marginBottom: 24 }}
                  >
                    <Typography.Title level={5}>Clases a generar</Typography.Title>
                    <Typography.Text type="secondary">
                      Selecciona grados, asignaturas y grupos. Al guardar la escuela
                      podrás confirmar la generación de estas clases y sus
                      programas quedarán asociados a la escuela.
                    </Typography.Text>
                    <div style={{ marginTop: 16 }}>
                      {/* initialSelection re-seeds this grid instance after the
                          loading swap unmounts the form (e.g. a failed create) —
                          the picks live on in generationSelections either way. */}
                      {/* existingClasses/existingGroups deliberately omitted: a
                          fresh school has neither, and the grid's stable empty
                          defaults avoid inline-[] identity churn in effect deps. */}
                      <SectionGradeProgramGrid
                        setGenerationSelections={setGenerationSelections}
                        groups={groups}
                        setGroups={setGroups}
                        initialSelection={initialGridSelection}
                        initialGroupsByGrade={initialGridGroups}
                        schoolProgramIds={existingProgramIds}
                      />
                    </div>
                  </div>
                )}
              </SchoolTab>
              {/* Edit-mode only: an access switch needs an existing school, and
                  the card sits OUTSIDE the form so it never submits with it.
                  SCRUM-916: is_active se hereda de la principal — el guard de
                  login lee la raíz —, así que en un plantel no hay switch. */}
              {effectiveSchoolId && !isCampus && (
                <SchoolStatusCard
                  schoolId={effectiveSchoolId}
                  isActive={isActive}
                  onStatusChange={setIsActive}
                />
              )}
              {/* Edit-mode only, and the card hides itself when the school has no
                  Kinder students (SCRUM-721). */}
              {effectiveSchoolId && (
                <KinderGraduationCard schoolId={effectiveSchoolId} />
              )}
            </TabPane>
            {/* SCRUM-916: los planteles son la ESTRUCTURA de la escuela, así
                que van pegados a Escuela y antes de Grupos. Sólo en la raíz:
                el backend rechaza anidar un plantel dentro de otro. */}
            {effectiveSchoolId && !isCampus && (
              <TabPane tab="Planteles" key="campuses">
                <CampusesTab schoolId={effectiveSchoolId} />
              </TabPane>
            )}
            {/* Solo mostrar las pestañas de Grupos, Clases y Usuarios si estamos editando una escuela existente (o si se acaba de crear en esta sesión) */}
            {effectiveSchoolId && (
              <>
                <TabPane tab="Grupos" key="groups">
                  <GroupsTab
                    groups={groups}
                    setGroups={setGroups}
                    schoolId={effectiveSchoolId}
                    // SCRUM-879: without this the catalogue in `originalGroups`
                    // stayed on the snapshot taken at load, and the grid unioned a
                    // just-deleted group back into the list this tab renders — a
                    // ghost that answers 404 when you try to delete it again.
                    onGroupsSaved={() => loadSchool(effectiveSchoolId)}
                    onRefreshClasses={refreshClasses}
                  />
                </TabPane>
                <TabPane tab="Clases" key="classes">
                  <ClassesTab
                    classes={classes}
                    setClasses={setClasses}
                    editingClassId={editingClassId}
                    setEditingClassId={setEditingClassId}
                    editingClass={editingClass}
                    setEditingClass={setEditingClass}
                    schoolAbbreviation={initialValues.abbreviation}
                    schoolId={effectiveSchoolId}
                    campuses={campuses}
                    groups={groups}
                    schoolPrograms={programSelected}
                    licensedPrograms={licensedPrograms}
                    generationSelections={generationSelections}
                    setGenerationSelections={setGenerationSelections}
                    setGroups={setGroups}
                    initialGridSelection={initialGridSelection}
                    initialGridGroups={initialGridGroups}
                    schoolProgramIds={existingProgramIds}
                    coordinator={coordinator}
                    existingGroups={originalGroups}
                    autoOpenGenerate={autoOpenGenerate}
                    onAutoOpenGenerateHandled={() => setAutoOpenGenerate(false)}
                    refreshClasses={refreshClasses}
                    onGenerated={async () => {
                      // Reload the full persisted school state (not just
                      // classes) so programSelected/licensedPrograms reflect the
                      // now-persisted SchoolToProgram links (incl. any
                      // backend-auto-linked program) and groups. No tab switch
                      // needed — generation now happens from within Clases.
                      await loadSchool(effectiveSchoolId);
                    }}
                  />
                </TabPane>
                <TabPane tab="Usuarios" key="users">
                  <UsersTab
                    classes={classes}
                    schoolId={effectiveSchoolId}
                    campuses={campuses}
                  />
                </TabPane>
              </>
            )}
            {/* SCRUM-916: calendario, tienda y dosificación son configuración
                compartida — viven en la escuela principal y una escritura con
                el id de un plantel responde 400. */}
            {effectiveSchoolId && !isCampus && (
              <>
                <TabPane tab="Calendario" key="calendar">
                  <CalendarTab
                    schoolId={effectiveSchoolId}
                    schoolName={initialValues.name}
                    mostrarPreescolar={schoolSections.mostrar_preescolar}
                    mostrarPrimaria={schoolSections.mostrar_primaria}
                    schoolClassrooms={classes.map((c) => ({
                      id: c.id,
                      name: c.class_code || c.custom_code || `Clase ${c.id}`,
                      program_id: c.program_id,
                      custom_id: c.custom_id,
                      grade_id: c.grade_id,
                    }))}
                  />
                </TabPane>
                <TabPane tab="Tienda" key="tienda">
                  <TiendaTab schoolId={effectiveSchoolId} schoolName={initialValues.name} />
                </TabPane>
                <TabPane tab="Dosificación personalizada" key="dosification">
                  <DosificationTab
                    schoolId={effectiveSchoolId}
                    schoolName={initialValues.name}
                    licensedPrograms={licensedProgramEntities}
                  />
                </TabPane>
              </>
            )}
          </Tabs>
          <Modal
            open={confirmOpen}
            title="Actualizar usuarios de los alumnos"
            className="regenerate-usernames-confirm"
            maskClosable={false}
            closable={!regenerating}
            keyboard={!regenerating}
            destroyOnClose
            onCancel={regenerating ? undefined : handleCancelConfirm}
            footer={[
              <Button
                key="cancel"
                disabled={regenerating}
                onClick={handleCancelConfirm}
              >
                Cancelar
              </Button>,
              <Button
                key="confirm"
                type="primary"
                loading={regenerating}
                onClick={handleConfirmRegenerate}
              >
                Guardar
              </Button>,
            ]}
          >
            <Alert
              id={REGENERATE_WARNING_ALERT_ID}
              type="warning"
              showIcon
              message="Los usuarios anteriores de los alumnos dejarán de funcionar."
              style={{ marginBottom: 16 }}
            />
            {previewLoading ? (
              <Spin size="small" />
            ) : preview ? (
              <Typography.Paragraph>
                Se actualizarán {preview.wouldUpdate} usuarios
                {preview.potentialConflicts > 0 &&
                  " — algunos nombres podrían recibir un sufijo numérico para evitar duplicados."}
                {preview.wouldFail > 0 && (
                  <>
                    <br />
                    {preview.wouldFail} alumnos no podrán actualizarse (sin
                    grado asignado).
                  </>
                )}
              </Typography.Paragraph>
            ) : null}
            <Checkbox
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
            >
              Actualizar también los usuarios de los alumnos existentes
            </Checkbox>
          </Modal>
          <Modal
            open={result !== null || resultError !== null}
            title={
              resultError || (result && result.failed.length > 0)
                ? "Actualización con errores"
                : "Usuarios actualizados"
            }
            destroyOnClose
            onCancel={regenerating ? undefined : handleDismissResult}
            footer={[
              resultError || (result && result.failed.length > 0) ? (
                <Button
                  key="retry"
                  loading={regenerating}
                  disabled={regenerating}
                  onClick={handleRetryRegenerate}
                >
                  Reintentar
                </Button>
              ) : null,
              <Button
                key="ok"
                type="primary"
                disabled={regenerating}
                onClick={handleDismissResult}
              >
                Entendido
              </Button>,
            ]}
          >
            {resultError && (
              <Alert
                type="error"
                showIcon
                message={resultError}
                style={{ marginBottom: 16 }}
              />
            )}
            {result && (
              <Alert
                type={result.failed.length > 0 ? "warning" : "success"}
                showIcon
                message={`Se actualizaron ${result.updated} usuarios`}
                description={`${result.skipped} sin cambios · ${result.total} alumnos`}
                style={{ marginBottom: 16 }}
              />
            )}
            {result && result.failed.length > 0 && (
              <List
                size="small"
                dataSource={result.failed}
                renderItem={(failure) => (
                  <List.Item key={failure.studentId}>
                    <List.Item.Meta
                      title={failure.name}
                      description={failure.reason}
                    />
                  </List.Item>
                )}
              />
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default SchoolForm;
